import React, { SyntheticEvent, useCallback, useRef, useState } from "react";

import ContainerFormButtons from "./ContainerFormButtons";

import { FormContainer, InputLabel } from "../../../../../constants/Styles";
import BinBarcodeField from "./BinBarcodeField";
import {
  ContainerSummaryContainer,
  MainContainer,
  Title,
} from "./PhysicalInventoryStyles";
import useContainerHistorySummary from "../../../../../utils/hooks/inventory/containerHistory";
import useMoveContainers from "../../../../../utils/hooks/inventory/moveContainers";
import Button from "../../../../common/Button";
import useTriggerSounds from "../../../../../utils/hooks/sounds";
import { useStringInput } from "../../../../../utils/hooks/inputs";
import ContainerSummary from "../../../../internal/parts/InventoryUpdates/ContainerSummary";
import {
  useCustomAlert,
  useCustomConfirm,
} from "../../../../../utils/hooks/alerts";
import { ItemType } from "../../../../internal/parts/InventoryUpdates/MoveContainerAction";
import ProjectsUsingPart from "../../../../internal/parts/InventoryUpdates/ProjectsUsingPart";
import useContainer from "../../../../../utils/hooks/inventory/getContainer";
import NumberInput from "../../../../common/NumberInput";
import { QtyFieldContainer } from "../../../../internal/parts/InventoryUpdates/InventoryUpdatesStyles";
import { ErrorText } from "../InventoryStyles";
import {
  PART_NOT_ON_BOM_CONFIRM,
  PART_NOT_ON_BOM_CONFIRM_TEXT,
  PART_NOT_ON_BOM_ERROR,
} from "../../../../../constants/errors";

const ZERO_QUANTITY_ERROR =
  "This container has an estimated quantity of 0. Please update the quantity before moving it.";
const UpdateContainerLocation = ({
  selectedContainers,
  itemType,
  onCancel,
  onFinishedEditing,
}: {
  selectedContainers: string[];
  itemType: ItemType;
  onCancel: () => void;
  onFinishedEditing: () => void;
}) => {
  const { value: binBarcode, bind: bindBinBarcode } = useStringInput("");
  const { triggerGoodSound, triggerBadSound } = useTriggerSounds();
  const { fetchHistory } = useContainerHistorySummary();
  const binInputRef = useRef<HTMLInputElement>(null);
  const customAlert = useCustomAlert();
  const customConfirm = useCustomConfirm();
  const { container, refreshContainer } = useContainer(
    itemType === ItemType.PARTS_CONTAINERS && selectedContainers[0]
      ? selectedContainers[0]
      : null,
  );
  const [containerQty, setContainerQty] = useState(
    container?.last_counted_amount || 1,
  );
  const [updateQtyField, setUpdateQtyField] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const onError = useCallback(
    (detail: string, retryAllowingPartNotOnBom?: () => void) => {
      triggerBadSound();

      if (detail === ZERO_QUANTITY_ERROR) {
        customAlert(detail);
        setUpdateQtyField(true);
      } else if (
        detail === PART_NOT_ON_BOM_ERROR &&
        retryAllowingPartNotOnBom
      ) {
        customConfirm(
          PART_NOT_ON_BOM_CONFIRM,
          retryAllowingPartNotOnBom,
          undefined,
          { confirmText: PART_NOT_ON_BOM_CONFIRM_TEXT },
        );
      } else {
        customAlert(detail, onCancel);
      }
    },
    [customAlert, customConfirm, onCancel, triggerBadSound],
  );

  const onSuccessfulMove = useCallback(() => {
    fetchHistory();
    triggerGoodSound();
    onFinishedEditing();
    if (container) {
      refreshContainer();
    }
  }, [
    fetchHistory,
    onFinishedEditing,
    triggerGoodSound,
    container,
    refreshContainer,
  ]);

  const { moveContainers, moveContainerAndUpdateQty, movingContainers } =
    useMoveContainers(onSuccessfulMove, onError);

  const onSubmit = (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (binBarcode) {
      if (container && updateQtyField) {
        const allocatedAmount = container.part_allocations.reduce(
          (totalAllocations, shipmentAllocation) => {
            totalAllocations += shipmentAllocation.quantity_allocated;
            return totalAllocations;
          },
          0,
        );

        if (!containerQty || containerQty < allocatedAmount) {
          setValidationMessage(
            "Container quantity must be a positive number and must be greater than its' allocated amount.",
          );
        } else {
          moveContainerAndUpdateQty(
            binBarcode,
            containerQty,
            container.flashpcb_container_barcode,
          );
        }
      } else {
        moveContainers(binBarcode, selectedContainers, itemType);
      }
    } else {
      triggerBadSound();
    }
  };

  const discardOrMarkAsMissing = () => {
    customConfirm(
      "Discarding these containers will zero them out and remove any activate allocations, meaning these projects may be in need of parts. Confirm to continue",
      () => {
        moveContainers(null, selectedContainers, itemType);
      },
    );
  };

  const renderContainerSummary = () => {
    if (selectedContainers.length === 1) {
      return (
        <ContainerSummary
          containerBarcode={selectedContainers[0] as string}
          onError={onError}
          displayAllocations={false}
        />
      );
    } else {
      selectedContainers.map((containerBarcode) => {
        return (
          <ContainerSummaryContainer key={containerBarcode}>
            <ContainerSummary
              containerBarcode={containerBarcode}
              onError={onError}
              displayAllocations={false}
            />
          </ContainerSummaryContainer>
        );
      });
    }
  };
  return (
    <MainContainer>
      <Title>Update container location</Title>
      {itemType === ItemType.PARTS_CONTAINERS && renderContainerSummary()}
      <FormContainer onSubmit={onSubmit}>
        <BinBarcodeField
          binBarcode={binBarcode}
          bind={bindBinBarcode}
          autoFocus
          ref={binInputRef}
        />
        {updateQtyField && container && (
          <QtyFieldContainer>
            {validationMessage && <ErrorText>{validationMessage}</ErrorText>}
            <InputLabel>Container qty:</InputLabel>
            <NumberInput
              value={containerQty}
              onChange={(newValue) => {
                setContainerQty(newValue);
              }}
              min={0}
            />
          </QtyFieldContainer>
        )}
        <ContainerFormButtons
          onCancel={onCancel}
          loading={movingContainers}
          isDisabled={!binBarcode}
          onSubmit={onSubmit}
        />
        {itemType === ItemType.PARTS_CONTAINERS && (
          <Button
            label={"Discard or mark as missing"}
            onClick={discardOrMarkAsMissing}
          />
        )}
        {itemType === ItemType.PARTS_CONTAINERS &&
          selectedContainers[0] !== undefined && (
            <ProjectsUsingPart containerBarcode={selectedContainers[0]} />
          )}
      </FormContainer>
    </MainContainer>
  );
};

export default UpdateContainerLocation;
